import { Button, FormNumber, FormPrice, RHF } from "@devhop/ui";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect } from "react";
import { useWatch } from "react-hook-form";
import type { PrivateLessonTemplateFormValues } from "./PrivateLessonTemplateForm";

const DEFAULT_TIER = {
  minPeople: 1,
  maxPeople: 2,
  pricePerHour: 380_000,
  pricePerHourOffSiteNear: null as number | null,
  pricePerHourOffSiteFar: null as number | null,
  extraPersonPrice: null as number | null,
};

export function PrivateLessonTemplateTiersEditor() {
  const { useFormContext, useFieldArray } = RHF;
  const { control, setValue } = useFormContext<PrivateLessonTemplateFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "tiers",
  });

  const defaultLocationKind = useWatch({
    control,
    name: "defaultLocationKind",
  });
  const showOffSitePrices = defaultLocationKind === "off_site";

  useEffect(() => {
    if (showOffSitePrices) return;
    for (let index = 0; index < fields.length; index++) {
      setValue(`tiers.${index}.pricePerHourOffSiteNear`, null, {
        shouldValidate: false,
      });
      setValue(`tiers.${index}.pricePerHourOffSiteFar`, null, {
        shouldValidate: false,
      });
    }
  }, [showOffSitePrices, fields.length, setValue]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-medium text-sm">Tier ລາຄາຕາມຈຳນວນຄົນ</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            append({
              minPeople: (fields[fields.length - 1]?.maxPeople ?? 0) + 1,
              maxPeople: (fields[fields.length - 1]?.maxPeople ?? 0) + 2,
              pricePerHour: 750_000,
              pricePerHourOffSiteNear: null,
              pricePerHourOffSiteFar: null,
              extraPersonPrice: null,
            })
          }
        >
          <PlusIcon className="mr-1 size-4" />
          ເພີ່ມ tier
        </Button>
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table
          className={`w-full border-collapse text-sm ${showOffSitePrices ? "min-w-[720px]" : "min-w-[480px]"}`}
        >
          <thead>
            <tr className="border-b text-left text-muted-foreground text-xs">
              <th className="p-2">Tier</th>
              <th className="p-2">ຄົນຂັ້ນຕ່ຳ</th>
              <th className="p-2">ຄົນສູງສຸດ</th>
              <th className="p-2">Studio ₭/ຊມ.</th>
              {showOffSitePrices ? (
                <>
                  <th className="p-2">ໃກ້ ₭/ຊມ.</th>
                  <th className="p-2">ໄກ ₭/ຊມ.</th>
                </>
              ) : null}
              <th className="p-2">Extra/ຄົນ/ຊມ.</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => (
              <tr key={field.id} className="border-b align-top">
                <td className="p-2 font-medium">{index + 1}</td>
                <td className="p-2">
                  <FormNumber
                    name={`tiers.${index}.minPeople`}
                    label=""
                    requiredMark
                    min={1}
                  />
                </td>
                <td className="p-2">
                  <FormNumber
                    name={`tiers.${index}.maxPeople`}
                    label=""
                    requiredMark
                    min={1}
                  />
                </td>
                <td className="p-2">
                  <FormPrice
                    name={`tiers.${index}.pricePerHour`}
                    label=""
                    requiredMark
                    currencySymbol="₭"
                  />
                </td>
                {showOffSitePrices ? (
                  <>
                    <td className="p-2">
                      <FormPrice
                        name={`tiers.${index}.pricePerHourOffSiteNear`}
                        label=""
                        currencySymbol="₭"
                      />
                    </td>
                    <td className="p-2">
                      <FormPrice
                        name={`tiers.${index}.pricePerHourOffSiteFar`}
                        label=""
                        currencySymbol="₭"
                      />
                    </td>
                  </>
                ) : null}
                <td className="p-2">
                  {index === fields.length - 1 ? (
                    <FormPrice
                      name={`tiers.${index}.extraPersonPrice`}
                      label=""
                      currencySymbol="₭"
                    />
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
                <td className="p-2">
                  {fields.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                    >
                      <Trash2Icon className="size-4" />
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {fields.length === 0 && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => append(DEFAULT_TIER)}
        >
          <PlusIcon className="mr-2 size-4" />
          ເພີ່ມ tier ທຳອິດ
        </Button>
      )}

      {fields.map((field, index) => (
        <div
          key={`mobile-${field.id}`}
          className="grid gap-3 rounded-lg border bg-muted/30 p-4 md:hidden"
        >
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm">Tier {index + 1}</span>
            {fields.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(index)}
              >
                <Trash2Icon className="size-4" />
              </Button>
            )}
          </div>
          <FormNumber
            name={`tiers.${index}.minPeople`}
            label="ຄົນຂັ້ນຕ່ຳ"
            requiredMark
            min={1}
          />
          <FormNumber
            name={`tiers.${index}.maxPeople`}
            label="ຄົນສູງສຸດ"
            requiredMark
            min={1}
          />
          <FormPrice
            name={`tiers.${index}.pricePerHour`}
            label="Studio ₭/ຊົ່ວໂມງ"
            requiredMark
            currencySymbol="₭"
          />
          {showOffSitePrices ? (
            <>
              <FormPrice
                name={`tiers.${index}.pricePerHourOffSiteNear`}
                label="ໃກ້ ₭/ຊົ່ວໂມງ"
                currencySymbol="₭"
              />
              <FormPrice
                name={`tiers.${index}.pricePerHourOffSiteFar`}
                label="ໄກ ₭/ຊົ່ວໂມງ"
                currencySymbol="₭"
              />
            </>
          ) : null}
          {index === fields.length - 1 ? (
            <FormPrice
              name={`tiers.${index}.extraPersonPrice`}
              label="ຄ່າຄົນເພີ່ມ/ຊມ. (tier ສຸດທ້າຍ)"
              currencySymbol="₭"
            />
          ) : null}
        </div>
      ))}

      {showOffSitePrices ? (
        <p className="text-muted-foreground text-xs">
          ຖ້າບໍ່ກຳນົດລາຄາ ໃກ້/ໄກ ຈະໃຊ້ລາຄາ Studio ເປັນຄ່າເລີ່ມຕົ້ນ
        </p>
      ) : null}
    </div>
  );
}
